import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import { getAllScenes } from '../../utils/novelUtils';
import CustomSelect from '../CustomSelect';

const INITIAL_FORM = { message_count: 1, next_scene: { scene_id: '', scene_type: 'static' }, states_trigger: { flags: [], scales: [] } };

const ContextEndingTab = ({ novelId, sceneId, scene, setScene, novel, loading, setLoading, setError, isProduction, sceneType = 'interactive' }) => {
  const [showContextEndingForm, setShowContextEndingForm] = useState(false);
  const [sceneSearch, setSceneSearch] = useState('');
  const [contextEndingFormData, setContextEndingFormData] = useState(INITIAL_FORM);
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading, sceneType);

  const getNextSceneUrl = (sid, sceneType) => {
    const sceneTypePath = sceneType === 'interactive' ? 'interactive_scene' : sceneType === 'open_world' ? 'open_world_scene' : 'static_scene';
    const mode = isProduction ? 'production_change' : 'building';
    return `/${mode}/${novelId}/${sceneTypePath}/${sid}`;
  };

  const allScenes = getAllScenes(novel);
  const getAvailableScenes = (sceneType, searchTerm = '') =>
    allScenes.filter(s => s.type === sceneType && (s.id === contextEndingFormData.next_scene.scene_id || s.name.toLowerCase().includes(searchTerm.toLowerCase()))).sort((a, b) => a.name.localeCompare(b.name));
  const getSceneName = (sid, sceneType) => allScenes.find(s => s.id === sid && s.type === sceneType)?.name || 'Неизвестная сцена';

  const handleAddContextEnding = () => {
    setContextEndingFormData(INITIAL_FORM);
    setSceneSearch('');
    setShowContextEndingForm(true);
  };

  const handleEditContextEndingClick = () => {
    if (scene?.context_ending) {
      setContextEndingFormData({
        message_count: scene.context_ending.message_count,
        next_scene: scene.context_ending.next_scene || { scene_id: '', scene_type: 'static' },
        states_trigger: scene.context_ending.states_trigger || { flags: [], scales: [] }
      });
    }
    setSceneSearch('');
    setShowContextEndingForm(true);
  };

  const handleSaveContextEnding = async () => {
    if (!contextEndingFormData.message_count || contextEndingFormData.message_count < 1) {
      setError('Введите корректное количество сообщений (не менее 1)');
      return;
    }
    setError(null);
    const newContextEnding = {
      message_count: contextEndingFormData.message_count,
      next_scene: contextEndingFormData.next_scene.scene_id ? {
        scene_id: contextEndingFormData.next_scene.scene_id,
        scene_type: contextEndingFormData.next_scene.scene_type
      } : null,
      states_trigger: contextEndingFormData.states_trigger
    };
    const success = await saveScene({ ...scene, context_ending: newContextEnding }, 'Концовка успешно сохранена!');
    if (success) setShowContextEndingForm(false);
  };

  const handleDeleteContextEnding = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить эту концовку?')) return;
    await saveScene({ ...scene, context_ending: null }, 'Концовка успешно удалена!');
  };

  const handleCancelContextEndingForm = () => {
    setShowContextEndingForm(false);
    setContextEndingFormData(INITIAL_FORM);
    setSceneSearch('');
    setError(null);
  };

  const updateFormFlags = (flags) => setContextEndingFormData(prev => ({ ...prev, states_trigger: { ...prev.states_trigger, flags } }));
  const updateFormScales = (scales) => setContextEndingFormData(prev => ({ ...prev, states_trigger: { ...prev.states_trigger, scales } }));

  return (
    <div className="isend-tab-content">
      <p className="isend-help">Настройте концовку, которая срабатывает после определенного количества сообщений пользователя</p>

      {scene?.context_ending ? (
        <div className="isend-context-card">
          <div className="isend-card">
            <div className="isend-card-head">
              <h4 className="isend-card-name">Контекстная концовка (после {scene.context_ending.message_count} сообщений)</h4>
              <div className="isend-card-actions">
                {scene.context_ending.next_scene && (
                  <Link className="isend-btn-link" to={getNextSceneUrl(scene.context_ending.next_scene.scene_id, scene.context_ending.next_scene.scene_type)}>
                    Перейти к сцене
                  </Link>
                )}
                <button className="isend-btn-edit" onClick={handleEditContextEndingClick} disabled={loading}>Редактировать контекстную концовку</button>
                <button className="isend-btn-delete" onClick={handleDeleteContextEnding} disabled={loading}>Удалить</button>
              </div>
            </div>
            <div className="isend-card-body">
              <div className="isend-details">
                <p><strong>Количество сообщений:</strong> {scene.context_ending.message_count}</p>
                {scene.context_ending.next_scene ? (
                  <p><strong>Следующая сцена:</strong>{' '}
                    <Link to={getNextSceneUrl(scene.context_ending.next_scene.scene_id, scene.context_ending.next_scene.scene_type)}>
                      {getSceneName(scene.context_ending.next_scene.scene_id, scene.context_ending.next_scene.scene_type)}
                    </Link>
                    {' '}({scene.context_ending.next_scene.scene_type})
                  </p>
                ) : (
                  <p><strong>Следующая сцена:</strong> Нет следующей сцены (конец истории)</p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="isend-no-context">
          <p>Для этой сцены пока не настроена контекстная концовка.</p>
          <div className="isend-add-wrap">
            <button className="isend-add-btn" onClick={handleAddContextEnding} disabled={loading}>
              <span className="button-icon">➕</span>
              Добавить контекстную концовку
            </button>
          </div>
        </div>
      )}

      {showContextEndingForm && (
        <div className="isend-overlay">
          <div className="isend-modal">
            <h3 className="isend-modal-title">{scene?.context_ending ? 'Редактировать контекстную концовку' : 'Добавить контекстную концовку'}</h3>

            <div className="isend-form-group">
              <label className="isend-label">Количество сообщений:</label>
              <input type="number" min="1" value={contextEndingFormData.message_count}
                onChange={(e) => setContextEndingFormData({ ...contextEndingFormData, message_count: parseInt(e.target.value) || 1 })}
                className="isend-input" disabled={loading} placeholder="Введите количество сообщений..." required />
              <p className="isend-form-help">Количество сообщений пользователя, после которых срабатывает эта концовка</p>
            </div>

            <div className="isend-form-group">
              <label className="isend-label">Триггер состояний:</label>
              <p className="isend-form-help">Настройте, какие флаги и весы изменить при достижении этой концовки</p>

              <div className="isend-trigger-section">
                <h5>Триггер флага:</h5>
                {contextEndingFormData.states_trigger.flags.map((flagTrigger, index) => (
                  <div key={index} className="isend-trigger-row">
                    <CustomSelect value={flagTrigger.flag_id || ''} onChange={(e) => { const f = [...contextEndingFormData.states_trigger.flags]; f[index] = { ...f[index], flag_id: e.target.value }; updateFormFlags(f); }} className="isend-input" disabled={loading}>
                      <option value="">Флаг...</option>
                      {(novel?.states?.flags || []).map((flag) => <option key={flag.id} value={flag.id}>{flag.name}</option>)}
                    </CustomSelect>
                    <CustomSelect value={flagTrigger.value !== undefined ? flagTrigger.value.toString() : 'true'} onChange={(e) => { const f = [...contextEndingFormData.states_trigger.flags]; f[index] = { ...f[index], value: e.target.value === 'true' }; updateFormFlags(f); }} className="isend-input" disabled={loading}>
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </CustomSelect>
                    <button type="button" onClick={() => updateFormFlags(contextEndingFormData.states_trigger.flags.filter((_, i) => i !== index))} className="isend-btn-cancel isend-trigger-remove" disabled={loading}>Удалить</button>
                  </div>
                ))}
                <button type="button" onClick={() => updateFormFlags([...contextEndingFormData.states_trigger.flags, { flag_id: '', value: true }])} className="isend-trigger-add" disabled={loading}>Добавить триггер флага</button>
              </div>

              <div className="isend-trigger-section">
                <h5>Триггер веса:</h5>
                {contextEndingFormData.states_trigger.scales.map((scaleTrigger, index) => (
                  <div key={index} className="isend-trigger-row">
                    <CustomSelect value={scaleTrigger.scale_id || ''} onChange={(e) => { const s = [...contextEndingFormData.states_trigger.scales]; s[index] = { ...s[index], scale_id: e.target.value }; updateFormScales(s); }} className="isend-input" disabled={loading}>
                      <option value="">Вес...</option>
                      {(novel?.states?.scales || []).map((scale) => <option key={scale.id} value={scale.id}>{scale.name}</option>)}
                    </CustomSelect>
                    <input type="text" value={scaleTrigger.change || ''} onChange={(e) => { const s = [...contextEndingFormData.states_trigger.scales]; s[index] = { ...s[index], change: e.target.value }; updateFormScales(s); }} placeholder="Используйте +3, -5, или 3 (для абсолютного значения)" className="isend-input" disabled={loading} />
                    <button type="button" onClick={() => updateFormScales(contextEndingFormData.states_trigger.scales.filter((_, i) => i !== index))} className="isend-btn-cancel isend-trigger-remove" disabled={loading}>Удалить</button>
                  </div>
                ))}
                <button type="button" onClick={() => updateFormScales([...contextEndingFormData.states_trigger.scales, { scale_id: '', change: '' }])} className="isend-trigger-add" disabled={loading}>Добавить триггер веса</button>
              </div>
            </div>

            <div className="isend-form-group">
              <label className="isend-label">Следующая сцена:</label>
              <div className="isend-next-scene">
                <div className="isend-form-group">
                  <label className="isend-label">Тип сцены:</label>
                  <CustomSelect value={contextEndingFormData.next_scene.scene_type} onChange={(e) => setContextEndingFormData({ ...contextEndingFormData, next_scene: { ...contextEndingFormData.next_scene, scene_type: e.target.value, scene_id: '' } })} className="app-select" disabled={loading}>
                    <option value="static">Статическая</option>
                    <option value="interactive">Интерактивная</option>
                    <option value="open_world">Открытый мир</option>
                  </CustomSelect>
                </div>
                <div className="isend-form-group">
                  <label className="isend-label">Выбор сцены:</label>
                  <input type="text" placeholder="Поиск сцен..." value={sceneSearch} onChange={(e) => setSceneSearch(e.target.value)} className="isend-input" style={{marginBottom: '10px'}} />
                  <CustomSelect value={contextEndingFormData.next_scene.scene_id} onChange={(e) => setContextEndingFormData({ ...contextEndingFormData, next_scene: { ...contextEndingFormData.next_scene, scene_id: e.target.value } })} className="app-select" disabled={loading}>
                    <option value="">Нет следующей сцены (конец истории)</option>
                    {getAvailableScenes(contextEndingFormData.next_scene.scene_type, sceneSearch).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </CustomSelect>
                </div>
              </div>
            </div>

            <div className="isend-form-actions">
              <button type="button" onClick={handleSaveContextEnding} disabled={loading} className="isend-btn-primary">{loading ? 'Загрузка...' : 'Сохранить'}</button>
              <button type="button" onClick={handleCancelContextEndingForm} disabled={loading} className="isend-btn-cancel">Отменить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextEndingTab;
